/* Raw Form ingredients — badges act as tabs.
   Cycles on its own until someone interacts, then stays put.
   On phones the badges are a swipeable carousel (rf-ingredients.css): the
   selected badge is scrolled to the left edge, and a swipe selects the badge
   it comes to rest on. */
class RFIngredients extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.rf-ingr__pick'));
    this.views = Array.from(this.querySelectorAll('.rf-ingr__view'));
    this.meter = this.querySelector('.rf-ingr__meter i');
    this.strip = this.querySelector('.rf-ingr__strip');
    if (this.tabs.length < 2) return;

    // Only a swipe the shopper makes selects a badge; scrolls we start
    // ourselves (bringing in the next badge) are ignored.
    this.swiping = false;
    const takeOver = () => {
      if (!this.isCarousel()) return;
      this.swiping = true;
      this.taken = true;
      this.stop();
    };
    this.strip.addEventListener('touchstart', takeOver, { passive: true });
    this.strip.addEventListener('pointerdown', takeOver);
    this.strip.addEventListener('wheel', takeOver, { passive: true });
    this.strip.addEventListener(
      'scroll',
      () => {
        clearTimeout(this.settle);
        this.settle = setTimeout(() => {
          if (!this.swiping) return;
          this.swiping = false;
          this.show(this.leadingTab().dataset.go);
        }, 140);
      },
      { passive: true }
    );

    this.taken = false;
    this.timer = null;
    this.speed = parseInt(this.dataset.speed, 10) || 4000;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.taken = true;
        this.stop();
        this.show(tab.dataset.go);
      });
      tab.addEventListener('keydown', (e) => this.onKey(e));
    });

    if (this.dataset.autoplay === 'true' && !this.reduced && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.taken) this.start();
            else this.stop();
          });
        },
        { threshold: 0.35 }
      );
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.stop();
    if (this.observer) this.observer.disconnect();
  }

  onKey(e) {
    const i = this.tabs.findIndex((t) => t === document.activeElement);
    if (i === -1) return;
    let next = null;
    if (e.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
    if (e.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
    if (next === null) return;
    e.preventDefault();
    this.taken = true;
    this.stop();
    this.tabs[next].focus();
    this.show(this.tabs[next].dataset.go);
  }

  start() {
    if (this.timer) return;
    this.timer = setInterval(() => {
      const i = this.tabs.findIndex((t) => t.dataset.go === this.dataset.active);
      const next = this.tabs[(i + 1) % this.tabs.length];
      this.show(next.dataset.go);
    }, this.speed);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  isCarousel() {
    return this.strip.scrollWidth > this.strip.clientWidth + 1;
  }

  // the badge resting at the strip's left edge
  leadingTab() {
    let best = this.tabs[0];
    let gap = Infinity;
    this.tabs.forEach((t) => {
      const d = Math.abs(t.offsetLeft - this.strip.scrollLeft);
      if (d < gap) {
        gap = d;
        best = t;
      }
    });
    return best;
  }

  bringIn(tab) {
    if (!this.isCarousel()) return;
    // scroll the strip only — scrollIntoView would also move the page
    this.strip.scrollTo({
      left: tab.offsetLeft,
      behavior: this.reduced ? 'auto' : 'smooth',
    });
  }

  show(id) {
    if (!id || this.dataset.active === id) return;
    const tab = this.tabs.find((t) => t.dataset.go === id);
    if (!tab) return;

    this.dataset.active = id;
    this.tabs.forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
    this.bringIn(tab);

    if (this.meter) {
      this.meter.style.transform = 'translateX(' + parseInt(tab.dataset.index, 10) * 100 + '%)';
    }

    this.views.forEach((view) => {
      const on = view.dataset.view === id;
      view.hidden = !on;
      if (!on) return;
      view.classList.remove('rf-ingr__swap');
      void view.offsetWidth;
      view.classList.add('rf-ingr__swap');
    });
  }
}

if (!customElements.get('rf-ingredients')) {
  customElements.define('rf-ingredients', RFIngredients);
}
